import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { tap, finalize } from 'rxjs/operators';
import { AccessTokenInfo } from './models/auth';
import { Router } from '@angular/router';

export interface StaffActivationRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface StaffActivationResponse {
  status: boolean;
  message: string;
  code: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _role = signal<string | null>(null);
  private _vendorId = signal<string | null>(null);
  private _accessTokenInfo = signal<AccessTokenInfo | null>(null);
  private _email = signal<string | null>(null);
  private _otpToken = signal<string | null>(null);

  readonly role = this._role.asReadonly();
  readonly vendorId = this._vendorId.asReadonly();
  readonly accessTokenInfo = this._accessTokenInfo.asReadonly();

  private baseAPI = environment.baseAPI;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.reinitializeTokens();
  }

  reinitializeTokens() {
    const token = this.getAccessToken();
    if (token) {
      try {
        const decoded = jwtDecode<AccessTokenInfo>(token);
        this._accessTokenInfo.set(decoded);
        const role =
          decoded.role ||
          decoded.roles ||
          (decoded as any)['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        if (role) {
          this._role.set(Array.isArray(role) ? role[0] : role);
        }
        this._vendorId.set(localStorage.getItem('vendorId'));
      } catch (error) {
        console.error('Failed to decode token on reinit, clearing tokens', error);
        this.clearTokens();
      }
    } else {
      this._accessTokenInfo.set(null);
      this._role.set(null);
      this._vendorId.set(null);
    }
  }

  getUserId() {
    const info: any = this._accessTokenInfo();
    const role = this.role();

    if (role === 'Staff') {
      const staffId = localStorage.getItem('StaffUserId') || localStorage.getItem('staffUserId') || localStorage.getItem('staffId');
      if (staffId) return staffId;
    }

    if (info) {
      return (
        info.id ||
        info.userId ||
        info.staffId ||
        info['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        info.sub
      );
    }

    return undefined;
  }

  getStaffId(): string | undefined {
    const role = this.role();
    if (role === 'Staff') {
      return localStorage.getItem('StaffUserId') || localStorage.getItem('staffUserId') || localStorage.getItem('staffId') || this.getUserId();
    }
    return this.getUserId();
  }


  setEmail(email: string) {
    this._email.set(email);
  }

  getEmail() {
    const info: any = this._accessTokenInfo();
    if (!info) return this._email();
    return (
      info.email ||
      info['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
      (info.sub && info.sub.includes('@') ? info.sub : null) ||
      this._email()
    );
  }

  setOtpToken(token: string) {
    this._otpToken.set(token);
  }

  getOtpToken() {
    return this._otpToken();
  }

  loginUser(loginData: { email: string; password: string }) {
    return this.http
      .post<any>(`${this.baseAPI}/Authentication/login`, loginData)
      .pipe(tap((res) => this.handleLoginSuccess(res.data)));
  }

  resetPassword(passwordData: { token: string; newPassword: string; confirmPassword: string }) {
    return this.http.post(`${this.baseAPI}/Authentication/forgot-password/confirm`, passwordData);
  }

  changePassword(passwordData: { currentPassword: string; newPassword: string; confirmPassword: string }) {
    return this.http.post(`${this.baseAPI}/Authentication/change-password/confirm`, passwordData);
  }

  createAdmin(data: any) {
    return this.http.post(`${this.baseAPI}/IdentityManagement/regular-admin/create-admin`, data);
  }

  handleLoginSuccess(data: any) {
    localStorage.removeItem('organisationName');
    localStorage.removeItem('organisationPhone');
    localStorage.removeItem('organisationEmail');
    localStorage.removeItem('organisationAddress');
    localStorage.removeItem('organisationHeadcount');
    localStorage.removeItem('organisationBillingContact');
    localStorage.removeItem('organisationIndustry');
    localStorage.removeItem('isProfileComplete');
    localStorage.removeItem('isSetupCompleteManual');

    const responseData = data?.data || data;
    const ssoData = responseData?.ssoData || responseData;
    const token =
      responseData?.token ||
      data?.token ||
      data?.accessToken ||
      ssoData?.token;
    const refreshToken =
      responseData?.refreshToken ||
      data?.refreshToken ||
      ssoData?.refreshToken;
    const role = responseData?.role || data?.data?.role || ssoData?.role;
    const organizationId = responseData?.organizationId || data?.data?.organizationId || ssoData?.organizationId;


    let userId: string | undefined;
    // let userId = responseData?.userId || ssoData?.userId;

    if (role === 'Staff' && responseData) {
      // The screenshot shows two userIds — we want the second one (the actual staff ID, responseData.userId)
      userId = responseData.userId;
    } else {
      userId = responseData?.userId || ssoData?.userId;
    }


    const vendorId =
      responseData?.vendorId ||
      responseData?.vendorProfileId ||
      ssoData?.vendorId ||
      ssoData?.vendorProfileId;


    if (token) {
      this.saveTokens(token, refreshToken);

      if (organizationId) {
        localStorage.setItem('organizationId', organizationId);
      }

      if (role === 'Vendor' && vendorId) {
        localStorage.setItem('vendorId', vendorId);
        this._vendorId.set(vendorId);
      }


      if (role === 'Staff' && userId) {
        localStorage.setItem('StaffUserId', userId);
        localStorage.setItem('staffUserId', userId);
      }




      const decoded = jwtDecode<AccessTokenInfo>(token);
      this._accessTokenInfo.set(decoded);
      this._role.set(role);
    } else {
      console.error('Token not found in login response', data);
    }
  }

  saveTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  saveOrganizationId(data: any) {
    localStorage.setItem('organizationId', data?.data?.organizationId);
  }

  getOrganizationId() {
    return localStorage.getItem('organizationId');
  }

  setVendorId(vendorId: string) {
    localStorage.setItem('vendorId', vendorId);
    this._vendorId.set(vendorId);
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  refreshToken(): Observable<any> {
    return this.http.post(`${this.baseAPI}/Authentication/refresh-token`, {
      refreshToken: this.getRefreshToken(),
    });
  }

  isImpersonating(): boolean {
    const info: any = this._accessTokenInfo();
    if (info && info.impersonatedBy) {
      return true;
    }
    return localStorage.getItem('isImpersonating') === 'true';
  }

  getImpersonatedBy(): string | null {
    const info: any = this._accessTokenInfo();
    return info?.impersonatedBy || null;
  }

  endImpersonation(): Observable<any> {
    return this.http.post<any>(`${this.baseAPI}/Organisations/end-impersonation`, {});
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('vendorId');
    this._vendorId.set(null);
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonatedOrgName');
    localStorage.removeItem('impersonatorToken');
    localStorage.removeItem('impersonatorRefreshToken');
    localStorage.removeItem('StaffUserId');
    localStorage.removeItem('staffUserId');
    localStorage.removeItem('staffId');

    localStorage.removeItem('adminName');
    localStorage.removeItem('organisationName');
    localStorage.removeItem('organisationPhone');
    localStorage.removeItem('organisationEmail');
    localStorage.removeItem('organisationAddress');
    localStorage.removeItem('organisationHeadcount');
    localStorage.removeItem('organisationBillingContact');
    localStorage.removeItem('organisationIndustry');
    localStorage.removeItem('isProfileComplete');
    localStorage.removeItem('isSetupCompleteManual');
  }

  getUserRole(): string[] {
    const role = this._role();
    if (!role) return [];
    return Array.isArray(role) ? role : [role];
  }

  verifyCurrentApplicationAndRole(currentApplication: string): boolean {
    const roles = this.getUserRole();

    if (currentApplication === 'super-admin') {
      if (roles.includes('SuperAdmin')) {
        this.router.navigate(['/dashboard']);
        return true;
      }
    } else if (currentApplication === 'admin') {
      if (roles.includes('Admin')) {
        this.router.navigate(['/dashboard']);
        return true;
      }
    } else if (currentApplication === 'vendor') {
      if (roles.includes('Vendor')) {
        this.router.navigate(['/dashboard']);
        return true;
      }
    } else if (currentApplication === 'staff') {
      this.router.navigate(['/home']);
      return true;
    }

    return false;
  }

  logout() {
    const accessToken = this.getAccessToken();
    const headers: { [header: string]: string } = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    this.http.post(`${this.baseAPI}/Authentication/logout`, {}, { headers }).subscribe({
      next: (res) => {
        console.log('Server logout successful:', res);
      },
      error: (err) => {
        console.error('Server logout failed:', err);
      }
    });

    this.clearTokens();
    this._accessTokenInfo.set(null);
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (decoded.exp) {
        return decoded.exp * 1000 > Date.now();
      }
      return true;
    } catch {
      return false;
    }
  }

  forgotPassword(email: string) {
    return this.http.post(`${this.baseAPI}/Authentication/forgot-password`, { email });
  }

  activateAccount(payload: { token: string; password?: string; confirmPassword?: string } | string) {
    const data = typeof payload === 'string' ? { token: payload } : payload;
    return this.http.post(`${this.baseAPI}/IdentityManagement/activate-account-by-token`, data);
  }

  staffActivation(payload: StaffActivationRequest): Observable<StaffActivationResponse> {
    return this.http.post<StaffActivationResponse>(
      `${this.baseAPI}/Authentication/forgot-password/confirm`,
      payload,
    );
  }

  otpToken(payload: { token: string; password: string; confrimPassword: string }) {
    return this.http.post(`${this.baseAPI}/IdentityManagement/reset-password`, payload);
  }

  verifyOtp(otpPayload: { token: string }) {
    return this.http.post(`${this.baseAPI}/IdentityManagement/verify-email`, otpPayload);
  }

  resendOtp(payload: { email: string }) {
    return this.http.post(`${this.baseAPI}/IdentityManagement/reset-password`, payload);
  }

  getAppUrl(appType: 'admin' | 'vendor' | 'super-admin' | 'staff'): string {
    const host = window.location.host;
    const protocol = window.location.protocol;

    if (host.includes('localhost')) {
      if (appType === 'super-admin') return `${protocol}//localhost:4200`;
      if (appType === 'admin') return `${protocol}//localhost:4201`;
      if (appType === 'vendor') return `${protocol}//localhost:4202`;
      if (appType === 'staff') return `${protocol}//localhost:4203`;
      return `${protocol}//localhost:4201`;
    }

    if (appType === 'super-admin') {
      if (host.includes('admin.')) return `${protocol}//${host.replace('admin.', 'super-admin.')}`;
      if (host.includes('admin')) return `${protocol}//${host.replace('admin', 'super-admin')}`;
      if (host.includes('vendor.')) return `${protocol}//${host.replace('vendor.', 'super-admin.')}`;
      if (host.includes('vendor')) return `${protocol}//${host.replace('vendor', 'super-admin')}`;
    } else if (appType === 'vendor') {
      if (environment.vendorAppUrl) {
        return environment.vendorAppUrl;
      }
      if (host.includes('admin.')) return `${protocol}//${host.replace('admin.', 'vendor.')}`;
      if (host.includes('admin')) return `${protocol}//${host.replace('admin', 'vendor')}`;
      if (host.includes('super-admin.')) return `${protocol}//${host.replace('super-admin.', 'vendor.')}`;
      if (host.includes('super-admin')) return `${protocol}//${host.replace('super-admin', 'vendor')}`;
    } else if (appType === 'admin') {
      if (environment.adminAppUrl) {
        return environment.adminAppUrl;
      }
      if (host.includes('vendor.')) return `${protocol}//${host.replace('vendor.', 'admin.')}`;
      if (host.includes('vendor')) return `${protocol}//${host.replace('vendor', 'admin')}`;
      if (host.includes('super-admin.')) return `${protocol}//${host.replace('super-admin.', 'admin.')}`;
      if (host.includes('super-admin')) return `${protocol}//${host.replace('super-admin', 'admin')}`;
    } else if (appType === 'staff') {
      if (environment.staffAppUrl) {
        return environment.staffAppUrl;
      }
      if (host.includes('admin.')) return `${protocol}//${host.replace('admin.', 'staff.')}`;
      if (host.includes('admin')) return `${protocol}//${host.replace('admin', 'staff')}`;
      if (host.includes('vendor.')) return `${protocol}//${host.replace('vendor.', 'staff.')}`;
      if (host.includes('vendor')) return `${protocol}//${host.replace('vendor', 'staff')}`;
    }

    if (appType === 'admin') {
      return environment.adminAppUrl || `${protocol}//${host}`;
    }
    if (appType === 'vendor') {
      const adminUrl = environment.adminAppUrl || '';
      if (adminUrl.includes('admin')) {
        return adminUrl.replace('admin', 'vendor');
      }
      return `${protocol}//${host}`;
    }
    if (appType === 'staff') {
      return environment.staffAppUrl || `${protocol}//${host}`;
    }

    return `${protocol}//${host}`;
  }

  getUserById(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseAPI}/IdentityManagement/users/${userId}`);
  }
}
